import React, {useEffect, useState} from 'react';
import {
  IFdCrop,
  IFdProductionPlan,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {apiGetFdCrops, apiUpdateFdProductionPlans} from 'constants/Api';
import {
  FdProductionPlanForm,
  Submission,
} from 'components/applications/FdProductionPlanForm';

const AddOrUpdateFdProductionPlan = (props: {
  user: ISupplierUserProfile;
  plan?: IFdProductionPlan;
  applicationId: number;
}) => {
  const [crops, setCrops] = useState<IFdCrop[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyUpdated, setHasSuccessfullyUpdated] = useState(false);

  const isAdding = !props.plan?.id;

  /* ─────────── Load crops once ─────────── */
  useEffect(() => {
    // Signal edit started
    reduxSetSidePanelEditState('editing');

    apiGetFdCrops()
      .then(setCrops)
      .catch((err) => console.error('failed to load crops', err));

    return () => {
      // reset when panel unmounts if not saved
      reduxSetSidePanelEditState('idle');
    };
  }, []);

  const handleCancel = () => reduxToggleSidePanel(false, null);

  /* ─────────── Submit handler ─────────── */
  const handleSubmit = (submission: Partial<Submission>) => {
    setIsLoading(true);

    apiUpdateFdProductionPlans([
      {
        id: props.plan?.id ? parseInt(props.plan.id) : undefined,
        applicationId: props.applicationId,
        cropId: parseInt(submission.cropId!),
        numberOfHectares: submission.numberOfHectares!,
        estimatedYield: submission.estimatedYield!,
        estimatedPrice: submission.estimatedPrice,
        plantingDate: new Date(submission.plantingDate!),
        plantPopulation: submission.plantPopulation,
        irrigation: submission.irrigation,
        areaOfDryLand: submission.areaOfDryLand,
        areaUnderIrrigation: submission.areaUnderIrrigation,
        notes: submission.notes ?? '',
      },
    ])
      .then(() => {
        setHasSuccessfullyUpdated(true);
        // Immediately signal that a save has occurred to trigger data refresh
        reduxSetSidePanelEditState('saved');
      })
      .catch((err) => {
        alert(
          'Failed to update production plan at the moment. Please try again later',
        );
        console.error('Failed to update production plan', err);
      })
      .finally(() => setIsLoading(false));
  };

  const canWrite = true; // adjust per-role if needed

  // Close panel after successful update
  useEffect(() => {
    if (hasSuccessfullyUpdated) {
      // Close the side panel after a short delay to allow data refresh
      setTimeout(() => {
        reduxToggleSidePanel(false, null);
      }, 100);
    }
  }, [hasSuccessfullyUpdated]);

  if (hasSuccessfullyUpdated) return null;

  /* ─────────── Form ─────────── */
  return (
    <FdProductionPlanForm
      productionPlan={props.plan}
      crops={crops}
      isLoading={isLoading}
      submitButtonTitle={isAdding ? 'Add' : 'Update'}
      onSubmit={handleSubmit}
      cancelButtonTitle="Cancel"
      onCancel={handleCancel}
      readonly={!canWrite}
    />
  );
};

export default AddOrUpdateFdProductionPlan;
